import React, { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/router";
import classnames from "classnames";
import Link from "next/link";
import { Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import Input from "@JohnLewisPartnership/jl-design-system/dist/elements/input/Input";
import { PrimaryButton, SecondaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import LoadingSpinner from "@/components/loading-spinner/LoadingSpinner";
import LocationFinderResult from "./LocationFinderResult";
import scrollToNode from "../../utils/scrollToNode";
import styles from "./location-finder.module.scss";

function LocationFinder({ locationSearch, results, searchTerm, error }) {
  const [inputValue, setInputValue] = useState(searchTerm);
  const [invalid, setInvalid] = useState(false);
  const [loading, setLoading] = useState(null);
  const resultCount = Number(results?.collectionPoints.length || 0);
  const topNewResultIndex = Math.max(resultCount - 5, 0);
  const router = useRouter();

  const handleSubmit = evt => {
    if (evt) {
      evt.preventDefault();
    }
    if (!inputValue || inputValue.trim() === "") {
      setInvalid(true);
    } else if (inputValue !== searchTerm) {
      setLoading("fresh");
      locationSearch(inputValue.trim());
      setInputValue(inputValue.trim());
    }
  };

  const handleInputChange = evt => {
    setInputValue(evt.target.value);
    if (invalid && evt.target.value && evt.target.value.trim() !== "") {
      setInvalid(false);
    }
  };

  const handleLoadMore = evt => {
    if (evt) {
      evt.preventDefault();
    }
    setLoading("extended");
    if (inputValue !== searchTerm) {
      setInputValue(searchTerm);
    }
    locationSearch(searchTerm, resultCount + 5);
  };

  const topNewResultRef = useCallback(node => {
    if (node !== null) {
      scrollToNode(node);
    }
  }, []);

  useEffect(() => {
    if (results || error) {
      setLoading(null);
    }
  }, [results, error]);

  return (
    <div className={styles.container}>
      {!loading && error && (
        <Message
          className={styles.messageBox}
          type="error"
          title={
            error?.code === "request.error" ? "Sorry, there was a problem" : "Sorry, we’ve had a technical problem"
          }
          body="Please refresh your page and try again."
          whiteBackground
        />
      )}
      {!loading && results?.totalFound === 0 && (
        <Message
          className={styles.messageBox}
          dataTest="message"
          type="error"
          title="No collection points available"
          body="There are no collection points within a 50 mile radius of your search. Please try using a different location"
          whiteBackground
        />
      )}
      <form onSubmit={handleSubmit} method="get">
        <label htmlFor="location-search" className={styles["location-finder__label"]}>
          Enter a shop name, postcode or town
        </label>
        <div className={styles["location-finder__input-container"]}>
          <Input
            id="location-search"
            className={classnames(styles["location-finder__input"], {
              error: invalid,
            })}
            value={inputValue}
            name="q"
            onChange={handleInputChange}
          />
          <PrimaryButton
            type="submit"
            className={styles["location-finder__button"]}
            data-test="location-finder-search-button"
          >
            Search
          </PrimaryButton>
        </div>
        {invalid && (
          <p className={styles["location-finder__validation-message"]}>Please enter a shop name or location</p>
        )}
        {!error && results && results.totalFound !== 0 && (
          <div className={styles["location-finder__results"]}>
            <h2 className={styles["location-finder__results-heading"]}>Your results for ‘{searchTerm}’</h2>
            {(!loading || loading === "extended") && (
              <ol className={styles["location-finder__results-list"]}>
                {results.collectionPoints.map((locationData, i) => (
                  <li
                    key={locationData.id}
                    className={styles["location-finder__results-list-item"]}
                    ref={topNewResultIndex === i ? topNewResultRef : null}
                  >
                    <LocationFinderResult {...locationData} />
                  </li>
                ))}
              </ol>
            )}
            {!loading && (
              <Link
                href={{
                  pathname: router.pathname,
                  query: {
                    ...router.query,
                    limit: Number(resultCount) + 5,
                  },
                }}
                scroll={false}
              >
                <SecondaryButton onClick={handleLoadMore}>Show more shops</SecondaryButton>
              </Link>
            )}
          </div>
        )}
        {loading && (
          <div className={styles["location-finder__spinner"]}>
            <LoadingSpinner />
          </div>
        )}
      </form>
    </div>
  );
}

export default LocationFinder;
